import { useCallback, useEffect, useRef, useState } from "react";

interface UseInfiniteScrollTriggerOptions {
  // Whether a page can be requested right now — must mirror every guard the
  // caller's onLoadMore applies, or a refused request is never retried.
  canLoadMore: boolean;
  onLoadMore: () => void;
  enabled?: boolean;
  // How far (px) outside the scroll root the sentinel still counts as reached.
  threshold?: number;
  // Rendered item count, so the sentinel is re-checked once a page lands.
  itemCount?: number;
}

// Requests the next page whenever the sentinel element (rendered after the
// last item) is within `threshold` px of the scroll root.
//
// IntersectionObserver only reports transitions: once the sentinel is in view
// it stays silent however many pages land, and a report that arrives while a
// page is loading is refused. So after every load, and whenever loading
// becomes possible again, the sentinel's position is measured directly and the
// next page is requested if it is still in reach — otherwise a short page, or
// a fast scroll to the bottom, leaves the list stalled with more rows to load.
//
// Callback refs (not plain useRef) so observation starts even when the root or
// sentinel mounts after the first render (e.g. behind a loading state).
export const useInfiniteScrollTrigger = <
  R extends HTMLElement = HTMLDivElement,
  S extends HTMLElement = HTMLDivElement
>({
  canLoadMore,
  onLoadMore,
  enabled = true,
  threshold = 100,
  itemCount
}: UseInfiniteScrollTriggerOptions) => {
  const [rootElement, setRootElement] = useState<R | null>(null);
  const [sentinelElement, setSentinelElement] = useState<S | null>(null);

  const canLoadMoreRef = useRef(canLoadMore);
  canLoadMoreRef.current = canLoadMore;
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  // Blocks a second request until the caller's pagination state moves (its
  // loading flag flips or a page lands); unrelated re-renders keep it set.
  const requestedRef = useRef(false);

  const isObservable = enabled && !!sentinelElement && !!rootElement;

  const requestNextPage = useCallback(() => {
    if (!canLoadMoreRef.current || requestedRef.current) return;
    requestedRef.current = true;
    onLoadMoreRef.current();
  }, []);

  const isSentinelInReach = useCallback(() => {
    if (
      !rootElement ||
      !sentinelElement ||
      sentinelElement.getClientRects().length === 0
    ) {
      return false;
    }
    const sentinel = sentinelElement.getBoundingClientRect();
    const rootBounds = rootElement.getBoundingClientRect();
    // Clipped to the viewport: a root whose height isn't bounded grows with its
    // content, so its own bottom edge would keep the sentinel in reach forever.
    const top = Math.max(rootBounds.top, 0);
    const bottom = Math.min(rootBounds.bottom, window.innerHeight);
    return (
      sentinel.top <= bottom + threshold && sentinel.bottom >= top - threshold
    );
  }, [rootElement, sentinelElement, threshold]);

  useEffect(() => {
    requestedRef.current = false;
  }, [canLoadMore, itemCount]);

  useEffect(() => {
    if (!isObservable || !sentinelElement) return;

    // A root without a content clip is measured by its whole box, so the
    // observer reports the sentinel of a growing root even below the viewport.
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries.some((entry) => entry.isIntersecting) &&
          isSentinelInReach()
        ) {
          requestNextPage();
        }
      },
      { root: rootElement, rootMargin: `${threshold}px` }
    );
    observer.observe(sentinelElement);
    return () => observer.disconnect();
  }, [
    isObservable,
    rootElement,
    sentinelElement,
    threshold,
    isSentinelInReach,
    requestNextPage
  ]);

  // A root that grows with its content never scrolls itself — an ancestor
  // does, which moves the sentinel without the observer seeing any change.
  useEffect(() => {
    if (!isObservable || !canLoadMore) return;

    const recheck = () => {
      if (isSentinelInReach()) requestNextPage();
    };
    document.addEventListener("scroll", recheck, {
      capture: true,
      passive: true
    });
    window.addEventListener("resize", recheck);
    return () => {
      document.removeEventListener("scroll", recheck, { capture: true });
      window.removeEventListener("resize", recheck);
    };
  }, [isObservable, canLoadMore, isSentinelInReach, requestNextPage]);

  useEffect(() => {
    if (isObservable && canLoadMore && isSentinelInReach()) requestNextPage();
  }, [
    isObservable,
    canLoadMore,
    itemCount,
    isSentinelInReach,
    requestNextPage
  ]);

  return { rootRef: setRootElement, sentinelRef: setSentinelElement };
};
